
import { FileSearch, RefreshCw, FileCheck, Users, GitBranch, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../Button";

const TransactionsContent = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Commercial Transaction Management</h1>
        <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
          AI-powered transaction management applications streamline complex business transactions for legal teams.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-20">
        <div className="prose prose-lg prose-invert max-w-4xl mx-auto">
          <p>
            AI-powered commercial transaction management applications for legal teams offer advanced features that streamline the handling of complex business transactions. The benefits include faster transaction processing, reduced manual errors, improved compliance, enhanced collaboration, and more time for strategic tasks like negotiation and client advising.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-blue-400">
              <FileSearch size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Automated Document Review</h3>
            <p className="text-white/70">
              AI accelerates the review of closing documents, purchase agreements, and other transactional paperwork, identifying risks and extracting key clauses for faster processing.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-purple-400">
              <FileCheck size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Contract Drafting and Revision</h3>
            <p className="text-white/70">
              These tools generate contract templates, suggest clauses, and track revisions across versions, ensuring accuracy and consistency during negotiations.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-green-400">
              <FileSearch size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Due Diligence</h3>
            <p className="text-white/70">
              AI simplifies the analysis of large datasets, flagging potential risks and ensuring compliance with regulatory standards.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-amber-400">
              <RefreshCw size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Workflow Automation</h3>
            <p className="text-white/70">
              Administrative tasks such as document organization, scheduling, and approval routing are automated to enhance efficiency.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-red-400">
              <Users size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Collaboration Tools</h3>
            <p className="text-white/70">
              Real-time collaboration features improve communication across legal, procurement, and sales teams during transactions.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-indigo-400">
              <Lock size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Regulatory Compliance</h3>
            <p className="text-white/70">
              AI monitors changes in regulations and ensures transactions meet all legal requirements, minimizing compliance risks and potential penalties.
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Curated Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Mining Deals</h3>
            <p className="text-white/70 mb-4">
              How Blakes Transformed Document Review to Streamline Due Diligence in Mining Deals
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.blakes.com/insights/case-studies/how-blakes-transformed-document-review-to-streamline-due-diligence-in-mining-deals/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Faster Deal Closings</h3>
            <p className="text-white/70 mb-4">
              Keating Muething & Klekamp close deals faster with iManage
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://imanage.com/resources/customer-stories/keating-muething-klekamp/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Deal Analysis</h3>
            <p className="text-white/70 mb-4">
              How Dealroom helps SAP analyze prospects and close more deals
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://dealroom.co/case-studies/sap-case-study', '_blank')}
            >
              Read Study
            </Button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-6">Streamline Your Transactions</h2>
        <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
          Discover how our AI-powered transaction management solutions can help your legal team close deals more efficiently.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact">
            <Button variant="glass" size="lg" hasAnimation={true}>Contact Us</Button>
          </Link>
          <Link to="/services/strategy">
            <Button variant="outline" size="lg">Explore Strategy Services</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TransactionsContent;
